import { Box, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { InlineErrorState, PagePanel } from '../../common';
import { SchemaMetadataFields } from '../schema-metadata-fields';
import {
  getInvalidSchemaRegistryMessage,
  type SchemaMetadataFormProps,
  toInitialMetadataDraft,
} from '../schema-metadata';
import { useSchemaMetadataForm } from './use-schema-metadata-form';

export const SchemaMetadataForm = ({
  formId,
  initialValues,
  onSubmit,
  readOnly = false,
  schemaRegistry,
}: SchemaMetadataFormProps) => {
  const [formError, setFormError] = useState<string | null>(null);

  const form = useSchemaMetadataForm({
    initialValues,
    onError: setFormError,
    onSubmit,
  });

  useEffect(() => {
    form.reset(toInitialMetadataDraft(initialValues));
    setFormError(null);
  }, [form, initialValues]);

  const registryValueError = getInvalidSchemaRegistryMessage(
    form.state.values,
    schemaRegistry,
  );

  const submit = () => {
    if (readOnly) {
      return;
    }

    if (registryValueError) {
      setFormError(null);
      return;
    }

    void form.handleSubmit();
  };

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Box p="xl">
        <PagePanel>
          <Stack spacing={32}>
            {readOnly ? (
              <Text color="dimmed" size="sm">
                Viewing a historical schema version. Settings are read-only.
              </Text>
            ) : null}

            <SchemaMetadataFields
              form={form}
              readOnly={readOnly}
              registry={schemaRegistry}
            />

            {!readOnly && registryValueError && (
              <InlineErrorState message={registryValueError} />
            )}

            {formError && <InlineErrorState message={formError} />}
          </Stack>
        </PagePanel>
      </Box>
    </form>
  );
};
